import { useContext } from "react";
import { PropContext } from "../layout/PropContext";
import { Image, Card, Button } from "react-bootstrap";
import Description from "./Description";

/*
Renders individual item in a list. 

-value: variable, is the value of a key:value pair in an array of objects generated by the page's/site's assets in src/assets/*. 
-id: string, title: string, and alt: string- variables come from the item's key. 
-itemType: string, is decided by the page being rendered from src/pages/* and chooses which switch case is rendered. 
-handleShowModal: function, is conditional for any item that will show a modal when clicked and is sourced from ./List.js whether it is implemented is decided by the page's assets.

Some itemTypes must have their values formatted in their src/pages/* in order to display correctly. See src/pages/Games.js for an example. Caused by utilizing separate files for each category of data rather than a single file for all data used.
*/
export default function Item({
  itemType,
  id,
  value,
  title,
  alt,
  handleShowModal,
}) {
  const { isMobile } = useContext(PropContext);

  const onclick = handleShowModal ? () => handleShowModal(id) : null;
  switch (itemType) {
    case "button": {
      return (
        <Button
          href={value.link ? value.link : ""}
          target="_blank"
          variant="dark"
          name={`btn ${id}`}
          id={`${id}`}
          className="add-column-border btn-group"
        >
          {value.child ? value.child : title}
        </Button>
      );
    }
    case "card": {
      //look into including multiple cards on smaller pages
      return (
        <Button
          href={value.link ? value.link : ""}
          target="_blank"
          variant="dark"
          name={title}
          id={id}
        >
          <Card bg="dark" text="light" className="card-style">
            <Card.Header>
              <Card.Title as="h3">{title}</Card.Title>
              {value.genres ? (
                <Card.Subtitle className="mb-2 text-muted">
                  {value.genres.join(" / ")}
                </Card.Subtitle>
              ) : null}
            </Card.Header>

            <Card.Body>
              {value.image ? <Image src={value.image} alt={alt} fluid /> : null}
              <Card.Text>
                <Description src={value.description} />
              </Card.Text>
              {value.comment ? (
                <Card.Text
                  className="blockquote-footer"
                  style={{ margin: "1em 0 0 0" }}
                >
                  {value.comment}
                </Card.Text>
              ) : null}
            </Card.Body>
          </Card>
        </Button>
      );
    }

    case "iframe": {
      return (
        <iframe
          name={title}
          id={id}
          src={value}
          width="100%"
          height="352"
          allow="fullscreen"
          loading="lazy"
          title={title}
        />
      );
    }
    case "image": {
      return value.includes("pdf") ? (
        <object
          name={title}
          id={id}
          alt={alt}
          data={value}
          type="application/pdf"
          style={{ width: "auto", height: "100%" }}
          className="contain"
        >
          <p>{title}</p>
        </object>
      ) : (
        <Image
          name={title}
          id={id}
          src={value}
          alt={alt}
          onClick={onclick ? onclick : () => window.open(value, "_blank")}
          className="pointer-on-hover"
          style={isMobile ? { maxWidth: "100%" } : null}
        />
      );
    }

    case "image_horizontal_list": {
      return value.includes("pdf") ? (
        <object
          name={title}
          id={id}
          data={value}
          alt={alt}
          type="application/pdf"
          className="image-container-horizontal-list"
        >
          <p>{title}</p>
        </object>
      ) : (
        <Image
          name={title}
          id={id}
          src={value}
          alt={alt}
          onClick={onclick ? onclick : () => window.open(value, "_blank")}
          className="pointer-on-hover image-container"
        />
      );
    }

    case "image_carousel": {
      return (
        <div
          name={title}
          id={id + "_container"}
          className="pointer-on-hover"
          style={{ justifySelf: "center" }}
        >
          <img src={value} alt={alt} onClick={onclick} className="contain" />
        </div>
      );
    }

    case "spotify": {
      return (
        <iframe
          name={title}
          id={id}
          src={`https://open.spotify.com/embed/playlist/${value}`}
          width="100%"
          height="352"
          frameBorder="0"
          allowFullScreen=""
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          title={title}
        />
      );
    }

    default: {
      new Error("Invalid element type for carousel-items");
    }
  }
}
